import { Suspense, useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { ACCESS_TIERS, PREMIUM_TIERS } from '../../config/constants';
import SchoolInviteBanner from '../School/SchoolInviteBanner';
import {
  FiGrid, FiBookOpen, FiFolder, FiTarget, FiEdit3,
  FiCreditCard, FiUser, FiShield, FiLogOut, FiMenu,
} from 'react-icons/fi';

/**
 * Sidebar shell for the logged-in / admin area.
 * Public marketing/auth/legal pages keep the top-nav Layout.
 */
const AppLayout = () => {
  const { currentUser, userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('teslp-sidebar-collapsed') === '1'; } catch { return false; }
  });

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem('teslp-sidebar-collapsed', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  // School members: note they've been active, for the Teachers list on the
  // school dashboard (the function writes at most hourly)
  const schoolId = userProfile?.schoolId;
  const verified = currentUser?.emailVerified;
  useEffect(() => {
    if (!schoolId || !verified) return;
    httpsCallable(functions, 'schoolTouch')().catch(() => {});
  }, [schoolId, verified]);

  // The logged-in Home keeps its own full-bleed marketing design system.
  const fullBleed = location.pathname === '/home';

  const tier = userProfile?.subscription?.tier;
  const subStatus = userProfile?.subscription?.status;
  const activeSub = ['active', 'trialing'].includes(subStatus);
  const isPremiumPlus = PREMIUM_TIERS.includes(tier) && activeSub;
  const hasPathwayAccess = ACCESS_TIERS.includes(tier) && activeSub;
  const isAdmin = !!userProfile?.isAdmin;

  const displayName = currentUser?.displayName || userProfile?.displayName || 'Account';
  const initials = displayName
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase() || 'U';

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Nav items — each with a visibility guard. (Dashboard is the logged-in home.)
  const navItems = [
    { to: '/dashboard', label: 'Dashboard', icon: FiGrid, show: true },
    { to: '/resources', label: 'Resources', icon: FiBookOpen, show: true },
    { to: '/collections', label: 'Collections', icon: FiFolder, show: isPremiumPlus },
    { to: '/pathways', label: 'Pathways', icon: FiTarget, show: hasPathwayAccess },
    { to: '/bespoke', label: 'Bespoke lessons', icon: FiEdit3, show: true },
    { to: '/subscription', label: 'Subscription', icon: FiCreditCard, show: true },
    { to: '/account', label: 'Account', icon: FiUser, show: true },
  ].filter((i) => i.show);

  const renderNavLink = ({ to, label, icon: Icon }, isCollapsed) => (
    <NavLink
      key={to}
      to={to}
      title={isCollapsed ? label : undefined}
      onClick={() => setMobileOpen(false)}
      className={({ isActive }) => [
        'flex items-center py-2.5 rounded-[10px] text-sm font-medium transition-colors',
        isCollapsed ? 'justify-center px-2' : 'gap-3 px-3',
        isActive
          ? 'bg-white/[0.15] text-white'
          : 'text-white/60 hover:text-white hover:bg-white/[0.08]',
      ].join(' ')}
    >
      <Icon className="w-[18px] h-[18px] flex-shrink-0" />
      {!isCollapsed && <span className="flex-1">{label}</span>}
    </NavLink>
  );

  // isCollapsed: icon-only rail; withToggle: show the desktop collapse button.
  const renderSidebar = (isCollapsed, withToggle) => (
    <>
      {/* Brand */}
      <div className={`flex items-center pt-3.5 pb-3.5 ${isCollapsed ? 'flex-col gap-2.5 px-2' : 'gap-2.5 px-3'}`}>
        <div className="bg-white rounded-lg p-1 flex items-center justify-center flex-shrink-0">
          <img src="/logo-icon.png" alt="TeachESLPro" className="h-8 w-8 object-contain" />
        </div>
        {!isCollapsed && (
          <span className="flex-1 min-w-0 font-heading font-bold text-white text-lg leading-none truncate">
            TeachESLPro
          </span>
        )}
        {withToggle && (
          <button
            onClick={toggleCollapsed}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="text-white/60 hover:text-white transition-colors p-1 flex-shrink-0"
          >
            <FiMenu className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 flex flex-col gap-0.5 overflow-y-auto overflow-x-hidden">
        {navItems.map((item) => renderNavLink(item, isCollapsed))}

        {isAdmin && (
          <>
            <div className="h-px bg-white/[0.12] mx-1 my-2.5" />
            {renderNavLink({ to: '/admin', label: 'Admin', icon: FiShield }, isCollapsed)}
          </>
        )}
      </nav>

      {/* User pill + sign out */}
      <div className="p-3">
        <div className={`flex items-center rounded-[10px] bg-white/10 ${isCollapsed ? 'justify-center p-2' : 'gap-3 p-3'}`}>
          <div className="w-8 h-8 rounded-full bg-gold text-primary flex items-center justify-center font-bold text-[13px] flex-shrink-0">
            {initials}
          </div>
          {!isCollapsed && (
            <>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-white truncate">{displayName}</div>
                <div className="text-[11px] text-white/50 truncate">
                  {currentUser?.email || 'ESL tutor'}
                </div>
              </div>
              <button
                onClick={handleLogout}
                aria-label="Sign out"
                className="text-white/50 hover:text-white transition-colors flex-shrink-0"
              >
                <FiLogOut className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
        {isCollapsed && (
          <button
            onClick={handleLogout}
            aria-label="Sign out"
            title="Sign out"
            className="mt-2 w-full flex justify-center py-1 text-white/50 hover:text-white transition-colors"
          >
            <FiLogOut className="w-4 h-4" />
          </button>
        )}
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-canvas text-muted">
      {/* Desktop sidebar */}
      <aside className={`hidden md:flex ${collapsed ? 'w-[76px]' : 'w-60'} flex-shrink-0 bg-primary text-white flex-col sticky top-0 self-start h-screen transition-[width] duration-200`}>
        {renderSidebar(collapsed, true)}
      </aside>

      {/* Mobile drawer (always full width) */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40 md:hidden"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="fixed inset-y-0 left-0 w-60 bg-primary text-white flex flex-col z-50 md:hidden">
            {renderSidebar(false, false)}
          </aside>
        </>
      )}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <div className="md:hidden sticky top-0 z-30 flex items-center gap-3 px-4 h-14 bg-primary text-white">
          <button onClick={() => setMobileOpen(true)} aria-label="Open menu">
            <FiMenu className="w-6 h-6" />
          </button>
          <div className="bg-white rounded-lg p-1 flex items-center justify-center flex-shrink-0">
            <img src="/logo-icon.png" alt="TeachESLPro" className="h-7 w-7 object-contain" />
          </div>
          <span className="font-heading font-bold">TeachESLPro</span>
        </div>

        <main className="flex-1 min-w-0">
          <SchoolInviteBanner />
          <Suspense
            fallback={
              <div className="flex items-center justify-center min-h-[50vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
              </div>
            }
          >
            {fullBleed ? (
              <Outlet />
            ) : (
              <div className="px-5 sm:px-8 lg:px-10 pt-6 sm:pt-8 pb-14 max-w-[1180px]">
                <Outlet />
              </div>
            )}
          </Suspense>
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
